import { Entity } from '@backstage/catalog-model';
import {
  coreExtensionData,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import {
  defaultEntityContentGroupDefinitions,
  EntityCardBlueprint,
  EntityContentBlueprint,
  EntityContentLayoutBlueprint,
  EntityContextMenuItemBlueprint,
  EntityContentGroupDefinitions,
  EntityHeaderLayoutBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import {
  mayHaveGatewayTab,
  providesApis,
  useGatewayTabAvailability,
} from './gatewayApis';
import { contentFilter, EntityRoute } from './tabs';
import type {
  EntityPageHeaderLayout,
  EntityPageMenuItem,
} from './ByxEntityPage';
import {
  canScheduleRefresh,
  refreshMenuIcon,
  useRefreshMenuItemProps,
} from './refreshMenuItem';
import { hasDependencies } from './DependenciesContent';
import {
  hasV2Page,
  isApi,
  isComponent,
  isGroup,
  isSystem,
} from './entityKinds';
import { hasApiProviders } from './cards/ApiComponentsCards';
import { hasSquadAbout } from './cards/SquadCards';
import { hasSystemAbout } from './cards/SystemCards';
import {
  ARGOCD_INSTANCE_ANNOTATION,
  ARGOCD_SELECTOR_ANNOTATION,
  hasDeployedEnvironments,
} from './argocd';
import { entityTranslations } from './translations';

export { isComponent };

const whenComponent = (test: (entity: Entity) => boolean) => (entity: Entity) =>
  isComponent(entity) && test(entity);

const whenApi = (test: (entity: Entity) => boolean) => (entity: Entity) =>
  isApi(entity) && test(entity);

/**
 * Runtime no-data guards of tabs, by extension id (AC-7): a tab whose
 * filter passes but whose data turns out empty is hidden, and its URL goes
 * to Visão geral (D-13).
 */
export const RUNTIME_TAB_GUARDS: Record<
  string,
  EntityRoute['useAvailability']
> = {
  'entity-content:catalog/byx-gateway': useGatewayTabAvailability,
};

/** Links has something to show: entity links or the Argo CD annotations. */
export function mayHaveLinks(entity: Entity): boolean {
  const annotations = entity.metadata.annotations ?? {};
  return (
    (entity.metadata.links?.length ?? 0) > 0 ||
    (!!annotations[ARGOCD_SELECTOR_ANNOTATION] &&
      !!annotations[ARGOCD_INSTANCE_ANNOTATION])
  );
}

/**
 * The entity page of every kind, in the v2 design (Story 3.1). The stock
 * page's own inputs (contents, context menu entries, header layouts) are
 * kept; only what it renders changes, and the "Catálogo" page-header bar
 * gives way to the entity header's back link (`noHeader`).
 *
 * Mirrored glue code (AD-1 [v3]): the factory below mirrors the loader of
 * `catalogEntityPage` in the installed
 * `@backstage/plugin-catalog/dist/alpha/pages.esm.js` (how contents, menu
 * items, header layouts and groups are read from the inputs and config).
 * The drift guard (`stockMirror.test.ts`) fails when that file changes.
 *
 * Stock settings this override does not support (AD-1 [v3]):
 * - `contentOrder` and `defaultContentOrder`: tabs follow the v2 order
 *   (`TAB_GROUP_ORDER`), and contents keep their natural order in a group;
 * - `showNavItemIcons`: the v2 tab bar has no icons;
 * - the legacy `headers` input (`EntityHeaderBlueprint`, deprecated): only
 *   `EntityHeaderLayoutBlueprint` layouts are used.
 * String filter expressions on contents are reduced to `kind:` / `type:`
 * terms and fail closed (`contentFilter` in `tabs.ts`).
 */
export const byxEntityPage = catalogPlugin
  .getExtension('page:catalog/entity')
  .override({
    factory: (originalFactory, { inputs, config }) => {
      const routes: EntityRoute[] = inputs.contents.map(output => ({
        path: output.get(coreExtensionData.routePath),
        title: output.get(EntityContentBlueprint.dataRefs.title),
        group: output.get(EntityContentBlueprint.dataRefs.group),
        if: contentFilter(
          output.get(EntityContentBlueprint.dataRefs.filterFunction),
          output.get(EntityContentBlueprint.dataRefs.filterExpression),
        ),
        useAvailability: RUNTIME_TAB_GUARDS[output.node.spec.id],
        children: output.get(coreExtensionData.reactElement),
      }));
      const menuItems: EntityPageMenuItem[] = inputs.contextMenuItems.map(
        item => ({
          data: item.get(EntityContextMenuItemBlueprint.dataRefs.data),
          node: item.node,
          filter:
            item.get(EntityContextMenuItemBlueprint.dataRefs.filterFunction) ??
            (() => true),
        }),
      );
      // Layouts with a filter win over the catch-all one, as in the stock page.
      const headerLayouts: EntityPageHeaderLayout[] = inputs.headerLayouts
        .map(layout => {
          const filter = layout.get(
            EntityHeaderLayoutBlueprint.dataRefs.filterFunction,
          );
          return {
            Component: layout.get(
              EntityHeaderLayoutBlueprint.dataRefs.component,
            ),
            filter: filter ?? (() => true),
            hasFilter: Boolean(filter),
          };
        })
        .sort((a, b) => Number(b.hasFilter) - Number(a.hasFilter))
        .map(({ Component, filter }) => ({ Component, filter }));
      const groupDefinitions: EntityContentGroupDefinitions =
        config.groups?.reduce(
          (all, group) => ({ ...all, ...group }),
          {} as EntityContentGroupDefinitions,
        ) ?? defaultEntityContentGroupDefinitions;
      return originalFactory({
        params: {
          noHeader: true,
          loader: async () => {
            const { ByxEntityPage } = await import('./ByxEntityPage');
            return (
              <ByxEntityPage
                routes={routes}
                menuItems={menuItems}
                headerLayouts={headerLayouts}
                groupDefinitions={groupDefinitions}
              />
            );
          },
        },
      });
    },
  });

/** One header for every kind (UI spec "Header bar"). */
export const byxEntityHeader = EntityHeaderLayoutBlueprint.make({
  name: 'byx-header',
  params: {
    loader: async () => (await import('./EntityHeader')).ByxEntityHeader,
  },
});

/** The v2 overview grid: Componente, API, Squad and Sistema. */
export const byxOverviewLayout = EntityContentLayoutBlueprint.make({
  name: 'byx-overview',
  params: {
    filter: hasV2Page,
    loader: async () => (await import('./OverviewLayout')).ByxOverviewLayout,
  },
});

export const byxAboutCard = EntityCardBlueprint.make({
  name: 'byx-about',
  params: {
    type: 'content',
    filter: isComponent,
    loader: () => import('./cards/AboutCard').then(m => <m.AboutCard />),
  },
});

export const byxRelationsCard = EntityCardBlueprint.make({
  name: 'byx-relations',
  params: {
    type: 'content',
    filter: isComponent,
    loader: () =>
      import('./cards/RelationsCard').then(m => <m.RelationsCard />),
  },
});

export const byxEnvironmentsCard = EntityCardBlueprint.make({
  name: 'byx-environments',
  params: {
    type: 'info',
    filter: whenComponent(hasDeployedEnvironments),
    loader: () =>
      import('./cards/EnvironmentsCard').then(m => <m.EnvironmentsCard />),
  },
});

export const byxGatewayRoutesCard = EntityCardBlueprint.make({
  name: 'byx-gateway-routes',
  params: {
    type: 'info',
    filter: whenComponent(providesApis),
    loader: () =>
      import('./cards/GatewayRoutesCard').then(m => <m.GatewayRoutesCard />),
  },
});

export const byxLinksCard = EntityCardBlueprint.make({
  name: 'byx-links',
  params: {
    type: 'info',
    filter: whenComponent(mayHaveLinks),
    loader: () => import('./cards/LinksCard').then(m => <m.LinksCard />),
  },
});

/** The Dependências tab (Story 3.3). */
export const byxDependenciesContent = EntityContentBlueprint.make({
  name: 'byx-dependencies',
  params: {
    path: '/dependencies',
    // Fallback only (code O9): the blueprint needs a plain string, and it
    // shows only where the v2 group label is absent. The visible label comes
    // from byxTranslationRef (`entity.tabs.dependencies`).
    title: 'Dependências',
    group: 'dependencies',
    filter: whenComponent(hasDependencies),
    loader: () =>
      import('./DependenciesContent').then(m => <m.DependenciesContent />),
  },
});

/** The Gateway tab of components and APIs (Story 3.4, AC-7). */
export const byxGatewayContent = EntityContentBlueprint.make({
  name: 'byx-gateway',
  params: {
    path: '/gateway',
    // Fallback only: the visible label is `entity.tabs.gateway`.
    title: 'Gateway',
    group: 'gateway',
    filter: mayHaveGatewayTab,
    loader: () => import('./GatewayContent').then(m => <m.GatewayContent />),
  },
});

/** The Componente API tab (Story 3.5). */
export const byxApiTabContent = EntityContentBlueprint.make({
  name: 'byx-api',
  params: {
    path: '/api',
    // Fallback only: the visible label is `entity.tabs.api`.
    title: 'API',
    group: 'api',
    filter: whenComponent(providesApis),
    loader: () => import('./ApiTabContent').then(m => <m.ApiTabContent />),
  },
});

/** The API Definição tab (Story 3.5, AC-5). */
export const byxDefinitionContent = EntityContentBlueprint.make({
  name: 'byx-definition',
  params: {
    path: '/definition',
    // Fallback only: the visible label is `entity.tabs.definition`.
    title: 'Definição',
    group: 'definition',
    filter: isApi,
    loader: () =>
      import('./DefinitionContent').then(m => <m.DefinitionContent />),
  },
});

/** API Sobre (Story 3.5). */
export const byxApiAboutCard = EntityCardBlueprint.make({
  name: 'byx-api-about',
  params: {
    type: 'content',
    filter: isApi,
    loader: () => import('./cards/ApiAboutCard').then(m => <m.ApiAboutCard />),
  },
});

/** API Fornecida por (AC-6): hidden when no component provides the API. */
export const byxApiProvidedByCard = EntityCardBlueprint.make({
  name: 'byx-api-provided-by',
  params: {
    type: 'info',
    filter: whenApi(hasApiProviders),
    loader: () =>
      import('./cards/ApiComponentsCards').then(m => <m.ApiProvidedByCard />),
  },
});

/** API Consumida por (AC-6): stays with its empty message. */
export const byxApiConsumedByCard = EntityCardBlueprint.make({
  name: 'byx-api-consumed-by',
  params: {
    type: 'info',
    filter: isApi,
    loader: () =>
      import('./cards/ApiComponentsCards').then(m => <m.ApiConsumedByCard />),
  },
});

/** Itens da squad (Story 4.1, AC-8). */
export const byxSquadItemsCard = EntityCardBlueprint.make({
  name: 'byx-squad-items',
  params: {
    type: 'content',
    filter: isGroup,
    loader: () => import('./cards/SquadCards').then(m => <m.SquadItemsCard />),
  },
});

/** Squad Sobre (Story 4.1): hidden when it has no field. */
export const byxSquadAboutCard = EntityCardBlueprint.make({
  name: 'byx-squad-about',
  params: {
    type: 'info',
    filter: hasSquadAbout,
    loader: () => import('./cards/SquadCards').then(m => <m.SquadAboutCard />),
  },
});

/** Partes do sistema (Story 4.2, AC-9). */
export const byxSystemPartsCard = EntityCardBlueprint.make({
  name: 'byx-system-parts',
  params: {
    type: 'content',
    filter: isSystem,
    loader: () =>
      import('./cards/SystemCards').then(m => <m.SystemPartsCard />),
  },
});

/** Sistema Sobre (Story 4.2): hidden when it has no block. */
export const byxSystemAboutCard = EntityCardBlueprint.make({
  name: 'byx-system-about',
  params: {
    type: 'info',
    filter: hasSystemAbout,
    loader: () =>
      import('./cards/SystemCards').then(m => <m.SystemAboutCard />),
  },
});

/** "Atualizar entidade" in the ⋯ menu (was the About card's refresh). */
export const byxRefreshMenuItem = EntityContextMenuItemBlueprint.make({
  name: 'byx-refresh-entity',
  params: {
    icon: refreshMenuIcon,
    useProps: useRefreshMenuItemProps,
    filter: canScheduleRefresh,
  },
});

export const entityModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    byxEntityPage,
    byxEntityHeader,
    byxOverviewLayout,
    byxAboutCard,
    byxRelationsCard,
    byxEnvironmentsCard,
    byxGatewayRoutesCard,
    byxLinksCard,
    byxDependenciesContent,
    byxGatewayContent,
    byxApiTabContent,
    byxDefinitionContent,
    byxApiAboutCard,
    byxApiProvidedByCard,
    byxApiConsumedByCard,
    byxSquadItemsCard,
    byxSquadAboutCard,
    byxSystemPartsCard,
    byxSystemAboutCard,
    byxRefreshMenuItem,
  ],
});

/**
 * pt-BR texts of the stock pieces on the entity pages (Story 2.2). The app's
 * translations input only accepts extensions of the app plugin itself.
 */
export const entityTranslationsModule = createFrontendModule({
  pluginId: 'app',
  extensions: entityTranslations,
});
